import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { UGCItemRow } from '@/common/model/ugcView'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
// 入力欄は標準 TextField / Textarea と同じトークンで揃える（枠: border-input）
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

/** Instagram接続状況（backend/routers/ugc_collection.py） */
interface IgStatus {
  connected: boolean
  account?: {
    username?: string
    followers_count?: number
    media_count?: number
    [key: string]: unknown
  }
  error?: string
}

/** 手動同期の結果（同 sync_instagram） */
interface IgSyncResult {
  ok: boolean
  sync_type?: string
  total_fetched?: number
  imported?: number
  skipped?: number
}

/** 許諾の集計（backend/routers/ugc_consent.py の get_consent_stats） */
interface ConsentStats {
  total: number
  granted: number
  denied: number
  pending: number
  grant_rate: number
  by_method: {
    consent_type: string
    total: number
    granted: number
    grant_rate: number
  }[]
}

const CONSENT_BADGE: Record<string, string> = {
  granted: 'bg-surface-success text-success',
  denied: 'bg-surface-critical text-critical',
  pending: 'bg-surface-warning text-warning',
}

const CONSENT_LABEL: Record<string, string> = {
  granted: '✅ 許諾済み',
  denied: '❌ 拒否',
  pending: '⏳ 未許諾',
}

export default function UGCInstagramCollection() {
  const { siteId } = useParams()
  const [status, setStatus] = useState<IgStatus | null>(null)
  const [items, setItems] = useState<UGCItemRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<IgSyncResult | null>(null)
  const [syncType, setSyncType] = useState('tagged')
  const [hashtag, setHashtag] = useState('')
  // 接続操作は「Instagram設定」画面に集約したので、この画面は状態の表示だけを行う
  const [consentStats, setConsentStats] = useState<ConsentStats | null>(null)
  const [sendingConsent, setSendingConsent] = useState<number | null>(null)
  const [selectedForConsent, setSelectedForConsent] = useState<Set<number>>(new Set())

  const fetchData = () => {
    setLoading(true)
    Promise.all([
      fetchRest<IgStatus>(`/api/sites/${siteId}/ugc/collection/instagram/status`),
      fetchRest<{ items?: UGCItemRow[]; total?: number }>(
        `/api/sites/${siteId}/ugc/items?source_type=instagram&page=${page}&per_page=20`
      ),
      fetchRest<ConsentStats>(`/api/sites/${siteId}/ugc/consent/stats`),
    ])
      .then(([s, i, cs]) => { setStatus(s); setItems(i.items || []); setTotal(i.total || 0); setConsentStats(cs) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [siteId, page])

  const handleSync = () => {
    setSyncing(true)
    setSyncResult(null)
    fetchRest<IgSyncResult>(`/api/sites/${siteId}/ugc/collection/instagram/sync`, {
      method: 'POST',
      body: JSON.stringify({ sync_type: syncType, hashtag: hashtag || null, limit: 25 }),
    })
      .then(d => { setSyncResult(d); fetchData() })
      .catch(() => setSyncResult({ ok: false }))
      .finally(() => setSyncing(false))
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📸 Instagram UGC収集'
        description='Instagramのタグ付き投稿・ハッシュタグ投稿を自動収集し、AI品質判定・使用許諾管理まで行います'
      />

      {/* 接続ステータス */}
      <div className={`${CARD} mb-4 flex items-center justify-between px-5 py-3.5`}>
        <div className='flex items-center gap-3'>
          <div className={`h-3 w-3 rounded-full ${status?.connected ? 'bg-fill-success' : 'bg-fill-critical'}`} />
          <span className={`text-sm font-semibold ${status?.connected ? 'text-success' : 'text-critical'}`}>
            {status?.connected ? 'Instagram 接続済み' : 'Instagram 未接続'}
          </span>
          {status?.account?.username && <span className='text-tertiary text-xs'>@{status.account.username}</span>}
          {status?.account?.followers_count != null && <span className='text-tertiary text-xs'>{status.account.followers_count.toLocaleString()} followers</span>}
        </div>
        {!status?.connected && (
          <a href={`/sites/${siteId}/ugc-instagram-settings`} className='inline-flex items-center rounded-lg border border-secondary bg-surface px-4 py-1.5 text-xs font-semibold text-secondary no-underline hover:bg-surface-hover'>
            Instagram設定で接続 →
          </a>
        )}
      </div>

      {/* 未接続時のガイド */}
      {!status?.connected && !loading && (
        <div className={`${CARD} mb-4 p-6`}>
          <h3 className='text-primary m-0 mb-3 text-base font-bold'>この画面でできること</h3>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            <div className='rounded-[10px] border border-secondary bg-surface-secondary p-4'>
              <div className='mb-2 text-2xl'>📥</div>
              <div className='text-primary mb-1 text-[13px] font-semibold'>UGC自動収集</div>
              <p className='text-secondary m-0 text-[11px] leading-relaxed'>Instagramのタグ付き投稿やハッシュタグ投稿を自動で収集します</p>
            </div>
            <div className='rounded-[10px] border border-secondary bg-surface-secondary p-4'>
              <div className='mb-2 text-2xl'>🤖</div>
              <div className='text-primary mb-1 text-[13px] font-semibold'>AI品質スコアリング</div>
              <p className='text-secondary m-0 text-[11px] leading-relaxed'>収集したUGCをAIが自動で品質判定し、スコアとカテゴリを付与します</p>
            </div>
            <div className='rounded-[10px] border border-secondary bg-surface-secondary p-4'>
              <div className='mb-2 text-2xl'>🏷️</div>
              <div className='text-primary mb-1 text-[13px] font-semibold'>商品紐づけ</div>
              <p className='text-secondary m-0 text-[11px] leading-relaxed'>UGC画像上にホットスポットで商品SKUを紐づけ、購入導線を作ります</p>
            </div>
            <div className='rounded-[10px] border border-secondary bg-surface-secondary p-4'>
              <div className='mb-2 text-2xl'>✅</div>
              <div className='text-primary mb-1 text-[13px] font-semibold'>使用許諾管理</div>
              <p className='text-secondary m-0 text-[11px] leading-relaxed'>投稿者にDMで使用許諾を取得し、承認済みUGCをECサイトに表示します</p>
            </div>
          </div>
          <p className='text-tertiary mt-4 text-center text-xs'>
            まずは<a href={`/sites/${siteId}/ugc-instagram-settings`} className='text-link font-semibold'>Instagram設定</a>からInstagramアカウントを接続してください
          </p>
        </div>
      )}

      {/* 同期操作 */}
      {status?.connected && (
        <div className={`${CARD} mb-4 p-5`}>
          <h3 className='text-primary m-0 text-sm font-semibold'>UGC同期</h3>
          <div className='mt-3 flex flex-wrap items-end gap-3'>
            <div className={FIELD}>
              <label className={LABEL}>同期タイプ</label>
              <select value={syncType} onChange={e => setSyncType(e.target.value)} className={`${INPUT} w-40`}>
                <option value='tagged'>タグ付き投稿</option>
                <option value='hashtag'>ハッシュタグ</option>
                <option value='account'>アカウント投稿</option>
              </select>
            </div>
            {syncType === 'hashtag' && (
              <div className={FIELD}>
                <label className={LABEL}>ハッシュタグ</label>
                <input value={hashtag} onChange={e => setHashtag(e.target.value)} placeholder='#ブランド名' className={`${INPUT} w-52`} />
              </div>
            )}
            <Button color={ButtonColor.PRIMARY} size='large' disabled={syncing} onClick={handleSync}>
              {syncing ? '同期中...' : '🔄 同期実行'}
            </Button>
          </div>
          {syncResult && (
            <div className={`mt-3 rounded-lg p-2.5 text-xs ${syncResult.ok ? 'bg-surface-success text-success' : 'bg-surface-critical text-critical'}`}>
              {syncResult.ok
                ? `✅ ${syncResult.total_fetched}件取得 → ${syncResult.imported}件インポート（${syncResult.skipped}件スキップ）`
                : '❌ 同期に失敗しました'}
            </div>
          )}
        </div>
      )}

      {/* 許諾統計 */}
      {consentStats && (
        <div className='mb-4 grid grid-cols-2 gap-2.5 md:grid-cols-4'>
          <div className={`${CARD} p-3 text-center`}><div className='text-secondary text-[10px]'>総UGC</div><div className='text-primary text-[22px] font-bold'>{consentStats.total}</div></div>
          <div className={`${CARD} p-3 text-center`}><div className='text-secondary text-[10px]'>許諾済み</div><div className='text-success text-[22px] font-bold'>{consentStats.granted}</div></div>
          <div className={`${CARD} p-3 text-center`}><div className='text-secondary text-[10px]'>未許諾</div><div className='text-warning text-[22px] font-bold'>{consentStats.pending}</div></div>
          <div className={`${CARD} p-3 text-center`}><div className='text-secondary text-[10px]'>許諾率</div><div className='text-emphasis text-[22px] font-bold'>{consentStats.grant_rate}%</div></div>
        </div>
      )}

      {/* 一括許諾リクエスト */}
      {selectedForConsent.size > 0 && (
        <div
          className={`${CARD} mb-4 flex items-center justify-between px-5 py-3`}
          style={{ borderLeft: '4px solid var(--color-fill-brand)' }}
        >
          <span className='text-primary text-[13px] font-semibold'>
            {selectedForConsent.size}件選択中
          </span>
          <div className='flex gap-2'>
            <Button
              color={ButtonColor.PRIMARY}
              onClick={() => {
                fetchRest(`/api/sites/${siteId}/ugc/consent/send`, {
                  method: 'POST',
                  body: JSON.stringify({ item_ids: [...selectedForConsent], consent_type: 'instagram_dm' }),
                }).then(() => { setSelectedForConsent(new Set()); fetchData() })
              }}
            >
              📩 一括DM許諾依頼
            </Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setSelectedForConsent(new Set())}>選択解除</Button>
          </div>
        </div>
      )}

      {/* UGC一覧 */}
      <div className={`${CARD} p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-primary m-0 text-sm font-semibold'>Instagram UGC ({total}件)</h3>
          {items.filter(i => i.consent_status === 'pending').length > 0 && (
            <button onClick={() => {
              const pendingIds = items.filter(i => i.consent_status === 'pending').map(i => i.id)
              setSelectedForConsent(new Set(pendingIds))
            }} className='text-emphasis cursor-pointer text-[11px] font-semibold'>
              未許諾を全選択 ({items.filter(i => i.consent_status === 'pending').length}件)
            </button>
          )}
        </div>
        {loading ? (
          <p className='text-tertiary py-10 text-center'>読み込み中...</p>
        ) : items.length === 0 ? (
          <p className='text-tertiary py-10 text-center'>Instagram UGCがありません。同期を実行してください。</p>
        ) : (
          <div className='grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3'>
            {items.map(item => {
              const consentBadge = CONSENT_BADGE[item.consent_status] || 'bg-surface-secondary text-secondary'
              const consentLabel = CONSENT_LABEL[item.consent_status] || item.consent_status
              const isSelected = selectedForConsent.has(item.id)
              return (
                <div key={item.id} className={`relative overflow-hidden rounded-[10px] bg-surface-secondary ${isSelected ? 'border-2 border-[var(--color-fill-brand)]' : 'border border-secondary'}`}>
                  {/* 選択チェック */}
                  {item.consent_status === 'pending' && (
                    <div onClick={() => {
                      setSelectedForConsent(prev => {
                        const next = new Set(prev)
                        next.has(item.id) ? next.delete(item.id) : next.add(item.id)
                        return next
                      })
                    }} className={`absolute top-1.5 left-1.5 z-[1] flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-full text-xs font-bold text-on-bg-fill ${isSelected ? 'bg-fill-brand' : 'border-2 border-secondary bg-surface'}`}>{isSelected ? '✓' : ''}</div>
                  )}

                  {item.media_url && <img src={item.media_url} alt='' className='aspect-square w-full object-cover' />}
                  <div className='p-2.5'>
                    <div className='mb-1 flex items-center gap-1.5'>
                      <span className='text-emphasis text-[11px] font-semibold'>{item.author_handle}</span>
                    </div>
                    <p className='text-secondary m-0 mb-1.5 line-clamp-2 text-[11px] leading-snug'>
                      {item.caption || '(キャプションなし)'}
                    </p>

                    {/* ステータスバッジ */}
                    <div className='mb-1.5 flex flex-wrap gap-1'>
                      <span className={`rounded px-1.5 py-px text-[9px] font-semibold ${consentBadge}`}>
                        {consentLabel}
                      </span>
                      {item.ai_score != null && (
                        <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[9px] font-semibold'>
                          AI: {item.ai_score}
                        </span>
                      )}
                      {item.product_external_id && (
                        <span className='bg-surface-warning text-warning rounded px-1.5 py-px text-[9px] font-semibold'>
                          🏷️ {item.product_external_id}
                        </span>
                      )}
                    </div>

                    {/* 許諾依頼ボタン */}
                    {item.consent_status === 'pending' && (
                      <button onClick={() => {
                        setSendingConsent(item.id)
                        fetchRest(`/api/sites/${siteId}/ugc/consent/send`, {
                          method: 'POST',
                          body: JSON.stringify({ item_ids: [item.id], consent_type: 'instagram_dm' }),
                        }).then(() => { setSendingConsent(null); fetchData() }).catch(() => setSendingConsent(null))
                      }} disabled={sendingConsent === item.id} className='bg-surface-emphasis text-emphasis w-full cursor-pointer rounded-md border border-secondary px-1.5 py-1.5 text-[11px] font-semibold'>
                        {sendingConsent === item.id ? '送信中...' : '📩 DM許諾依頼'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {total > 20 && (
          <div className='mt-4 flex items-center justify-center gap-2'>
            <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← 前へ</Button>
            <span className='text-secondary px-3 py-1.5 text-xs'>{page} / {Math.ceil(total / 20)}</span>
            <Button color={ButtonColor.SECONDARY} disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>次へ →</Button>
          </div>
        )}
      </div>
    </Main>
  )
}
